// userTypes.ts

import { FieldValue } from 'firebase/firestore';

// Base user interface with common properties
export interface BaseUser {
  id: string;
  name: string;
  email: string;
  accountVerification: {
    emailVerified: boolean;
    identityVerified: boolean;
    phoneVerified: boolean;
  };
  isPlatformAdmin?: boolean;
  phone: string;
  photoUrl?: string;
  accountCreationDate: FieldValue;
  bio: string;
  communicationPreferences: {
    emailOffers: boolean;
    pushNotifications: boolean;
  };
  engagementEmailsEnabled?: boolean;
  marketingConsent: boolean;
  fsboProfiles?: {
    progress: {
      hasSolicitor: boolean;
      hasBER: boolean;
      hasTitleDeeds: boolean;
      hasPlanningDocs: boolean;
      hasComplianceCerts: boolean;
      hasLPTClearance: boolean;
      hasMortgageInfo: boolean;
      hasManagementInfo: boolean;
      hasFixturesList: boolean;
      hasKeysReady: boolean;
    };
    progressHistory?: Record<string, number>;
  };
  listingPreferences: {
    showMobile: boolean;
    showEmail: boolean;
  };
  role: 'landlord' | 'renter' | 'agent' | 'fsbo';
}

// Landlord type
export interface Landlord extends BaseUser {
  role: 'landlord';
  company: string;
  psraLicence?: string;
}
export interface PropertyAgent extends BaseUser {
  role: 'agent';
  company: string;
  psraLicence?: string;
}

// Renter type
export interface Renter extends BaseUser {
  role: 'renter';
  preferredLocation: string;
}

// FSBO type (For Sale By Owner)
export interface Fsbo extends BaseUser {
  role: 'fsbo';
  fsboProfiles: {
    progress: {
      hasSolicitor: boolean;
      hasBER: boolean;
      hasTitleDeeds: boolean;
      hasPlanningDocs: boolean;
      hasComplianceCerts: boolean;
      hasLPTClearance: boolean;
      hasMortgageInfo: boolean;
      hasManagementInfo: boolean;
      hasFixturesList: boolean;
      hasKeysReady: boolean;
    };
    progressHistory?: Record<string, number>;
  };
}

// User type that can be Landlord, PropertyAgent, Renter, or Fsbo
export type User = Landlord | Renter | PropertyAgent | Fsbo;

// Type guard to check if a user is a Landlord
export function isLandlord(user: User | null): user is Landlord {
  if (user == null) return false;
  return user.role === 'landlord';
}

// Type guard to check if a user is a Renter
export function isRenter(user: User | null): user is Renter {
  if (user == null) return false;
  return user.role === 'renter';
}

// Type guard to check if a user is a PropertyAgent
export function isAgent(user: User | null): user is PropertyAgent {
  if (user == null) return false;
  return user.role === 'agent';
}

// Type guard to check if a user is a FSBO seller
export function isFsbo(user: User | null): user is Fsbo {
  if (user == null) return false;
  return user.role === 'fsbo';
}

// Type guard to check if a user is a Landlord or Agent
export function isAgentOrisLandlord(
  user: User | null
): user is Renter | PropertyAgent {
  if (user == null) return false;
  return user.role === 'landlord' || user.role === 'agent';
}
